import React, { useState, useCallback } from 'react';
import { listProjects, deleteProject } from '../utils/projectStore';
import './ProjectsPanel.css';

/**
 * ProjectsPanel — bottom-left "projects" toggle. Projects are live
 * documents: "+ new project" starts a blank one, clicking a project opens
 * it for editing, and the app auto-saves changes back to the open project.
 * "save current as project" captures an unsaved ad-hoc session.
 */
const ProjectsPanel = ({ activeId, activeName, canSaveCurrent, onLoad, onNew, onSaveCurrent, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [mode, setMode] = useState(null); // 'new' | 'current' | null
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setProjects(await listProjects());
    } catch {
      setProjects([]);
    }
  }, []);

  const toggle = async (e) => {
    e.currentTarget.blur();
    const next = !open;
    setOpen(next);
    setMode(null);
    if (next) refresh();
  };

  const startMode = (m) => {
    setMode(m);
    setName('');
  };

  const commit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      if (mode === 'new') await onNew(trimmed);
      else await onSaveCurrent(trimmed);
      setMode(null);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const load = async (p) => {
    if (busy) return;
    setBusy(true);
    try {
      await onLoad(p.id);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (e, p) => {
    e.stopPropagation();
    await deleteProject(p.id);
    if (onDeleted) onDeleted(p.id);
    refresh();
  };

  return (
    <div className="projects-panel">
      {open && (
        <div className="projects-list">
          {projects.length === 0 && !mode && (
            <div className="projects-empty">no projects yet</div>
          )}
          {projects.map((p) => (
            <div
              key={p.id}
              className={`projects-row${p.id === activeId ? ' active' : ''}`}
              onClick={() => load(p)}
            >
              {p.id === activeId && <span className="projects-row-dot" />}
              <span className="projects-row-name">{p.name}</span>
              <button
                type="button"
                className="projects-row-delete"
                onClick={(e) => remove(e, p)}
                title="delete project"
              >
                ×
              </button>
            </div>
          ))}
          {mode ? (
            <div className="projects-save-row">
              <input
                className="projects-save-input"
                value={name}
                autoFocus
                placeholder={mode === 'new' ? 'new project name' : 'save session as…'}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') commit();
                  if (e.key === 'Escape') setMode(null);
                }}
              />
              <button type="button" className="projects-save-confirm" onClick={commit}>
                {busy ? '…' : mode === 'new' ? 'create' : 'save'}
              </button>
            </div>
          ) : (
            <>
              <button type="button" className="projects-save-btn" onClick={() => startMode('new')}>
                + new project
              </button>
              {canSaveCurrent && !activeId && (
                <button type="button" className="projects-save-btn" onClick={() => startMode('current')}>
                  save current as project
                </button>
              )}
            </>
          )}
        </div>
      )}
      <button type="button" className="projects-toggle" onClick={toggle}>
        projects
        {activeName && <span className="projects-toggle-active"> · {activeName}</span>}
      </button>
    </div>
  );
};

export default ProjectsPanel;
